import type { TableCatalogModel } from '.';
import { filterTableKeys } from './filterTableKeys';
import {
    buildChainNodes,
    compareKeysInChainOrder,
    descendantsOf,
    resolveAncestors,
    visibleChainNodes,
} from './lineage';
import type { ChainAncestor, ChainNode } from './lineage';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type { Unsubscribable } from 'rxjs';
import { useNextGenProxy } from 'src/lib/featureFlags/flags/useNextGenProxy';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getTableServersQuery } from 'src/queries/tableServers';
import type { TableServerInfo, TablesInfo } from 'src/queries/tableServers';
import type { ReactBindings } from 'src/types/bindings';

export interface TableCatalogModelDeps {
    proxyClientService: ReactBindings['ProxyClientService'];
    queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];
    featureFlagService: ReactBindings['FeatureFlagService'];
    /** The `/tables` endpoint is superuser-only; the query is disabled for anyone else. */
    isSuperuserCapable: ReadonlySignal<boolean>;
}

/**
 * Two key sources are unioned on purpose: `keys$` is scoped to what the proxy session is entitled
 * to, which follows the superuser-mode toggle and is refetched whenever it changes. The `/tables`
 * REST snapshot is therefore the authoritative list and `keys$` is the live delta on top of it.
 */
export class TableCatalogModelImpl implements TableCatalogModel {
    readonly #disposableStack = new DisposableStack();
    readonly #liveKeys = signal<readonly string[]>([]);
    readonly #search = signal('');
    readonly #serverFilter = signal('');
    readonly #collapsedChainKeys = signal<ReadonlySet<string>>(new Set());
    readonly #tablesQuery: QueryObserverModel<TablesInfo, Error, TablesInfo>;
    readonly #isSuperuserCapable: ReadonlySignal<boolean>;

    readonly keys: ReadonlySignal<readonly string[]>;
    readonly filteredKeys: ReadonlySignal<readonly string[]>;
    readonly servers: ReadonlySignal<readonly string[]>;
    readonly tablesInfo: ReadonlySignal<TablesInfo | undefined>;
    readonly isFetching: ReadonlySignal<boolean>;
    readonly chainNodes: ReadonlySignal<readonly ChainNode[]>;

    constructor(deps: TableCatalogModelDeps) {
        this.#isSuperuserCapable = deps.isSuperuserCapable;
        const useNgProxy = deps.featureFlagService.getSignal(useNextGenProxy);
        const queryOptions = computed<QueryObserverOptions<TablesInfo, Error, TablesInfo>>(() => ({
            ...getTableServersQuery(useNgProxy.value),
            enabled: deps.isSuperuserCapable.value,
        }));
        this.#tablesQuery = deps.queryObserverModelFactory<TablesInfo, Error, TablesInfo>(queryOptions);
        this.#disposableStack.use(this.#tablesQuery);

        this.tablesInfo = computed(() => this.#tablesQuery.signal.value.data);
        this.isFetching = computed(() => this.#tablesQuery.signal.value.isFetching);

        this.keys = computed(() => {
            const snapshot = this.tablesInfo.value;
            const union = new Set(this.#liveKeys.value);
            if (snapshot !== undefined) {
                for (const key of Object.keys(snapshot)) {
                    union.add(key);
                }
            }
            return [...union].sort(compareKeysInChainOrder);
        });
        this.servers = computed(() => {
            const snapshot = this.tablesInfo.value;
            if (snapshot === undefined) {
                return [];
            }
            const servers = new Set<string>();
            for (const info of Object.values(snapshot)) {
                for (const server of info.servers) {
                    servers.add(server.id);
                }
            }
            return [...servers].sort();
        });
        this.filteredKeys = computed(() =>
            filterTableKeys({
                keys: this.keys.value,
                search: this.#search.value,
                tablesInfo: this.tablesInfo.value,
                tableServerFilter: this.#serverFilter.value,
            }),
        );
        const allChainNodes = computed(() => buildChainNodes(this.filteredKeys.value));
        this.chainNodes = computed(() => visibleChainNodes(allChainNodes.value, this.#collapsedChainKeys.value));

        // Follow the proxy target so a transport hot-swap re-attaches the live key stream.
        const target = deps.proxyClientService.target;
        this.#disposableStack.defer(
            effect(() => {
                const { client } = target.value;
                // A swapped transport starts from an empty live set; its own keys$ repopulates it.
                this.#liveKeys.value = [];
                const subscription: Unsubscribable = client.keys$.subscribe((keys) => {
                    this.#liveKeys.value = [...new Set(keys)];
                });
                return () => subscription.unsubscribe();
            }),
        );
    }

    get search(): ReadonlySignal<string> {
        return this.#search;
    }

    get serverFilter(): ReadonlySignal<string> {
        return this.#serverFilter;
    }

    get collapsedChainKeys(): ReadonlySignal<ReadonlySet<string>> {
        return this.#collapsedChainKeys;
    }

    toggleChainCollapsed(key: string): void {
        const next = new Set(this.#collapsedChainKeys.value);
        if (!next.delete(key)) {
            next.add(key);
        }
        this.#collapsedChainKeys.value = next;
    }

    ancestorsOf(key: string): ChainAncestor[] {
        return resolveAncestors(this.keys.value, key);
    }

    descendantsOf(key: string): string[] {
        return descendantsOf(this.keys.value, key);
    }

    setSearch(search: string): void {
        this.#search.value = search;
    }

    setServerFilter(server: string): void {
        this.#serverFilter.value = server;
    }

    serverInfo(key: string): TableServerInfo | undefined {
        return this.tablesInfo.value?.[key];
    }

    refresh(): void {
        // refetch() bypasses `enabled`; the endpoint is superuser-only, so do not poke it otherwise.
        if (!this.#isSuperuserCapable.value) {
            return;
        }
        void this.#tablesQuery.queryObserver.refetch();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
